if (!customElements.get('eg-video-carousel')) {
  class EgVideoCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.muted = true;
      this.popup = null;
      this.popupVideo = null;
      this.progressSegments = [];
    }

    connectedCallback() {
      const dataEl = this.querySelector('.eg-video-carousel__data');
      if (!dataEl) return;
      try {
        this.videos = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('eg-video-carousel: invalid JSON data', e);
        return;
      }

      this.list = this.querySelector('.eg-video-carousel__list');
      this.prevBtn = this.querySelector('.eg-video-carousel__arrow--prev');
      this.nextBtn = this.querySelector('.eg-video-carousel__arrow--next');

      this.querySelectorAll('[data-video-index]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const idx = parseInt(btn.dataset.videoIndex, 10);
          if (!Number.isNaN(idx)) this.openPopup(idx);
        });
      });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollList(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollList(1));
      if (this.list) {
        this.list.addEventListener('scroll', () => this.updateArrows(), { passive: true });
        this.updateArrows();
      }

      this.onKeydown = (e) => {
        if (e.key === 'Escape' && this.popup && this.popup.classList.contains('is-active')) {
          this.closePopup();
        }
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.popup && this.popup.parentNode) {
        this.popup.parentNode.removeChild(this.popup);
      }
    }

    scrollList(direction) {
      if (!this.list) return;
      const item = this.list.querySelector('.eg-video-carousel__item');
      if (!item) return;
      const styles = window.getComputedStyle(this.list);
      const gap = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;
      const step = item.offsetWidth + gap;
      this.list.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    updateArrows() {
      if (!this.list) return;
      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      const atStart = this.list.scrollLeft <= 2;
      const atEnd = this.list.scrollLeft >= maxScroll - 2;
      if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', atStart);
      if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', atEnd);
    }

    ensurePopup() {
      if (this.popup) return;
      const tpl = this.querySelector('.eg-video-carousel__popup-template');
      if (!tpl) return;
      this.popup = tpl.content.firstElementChild.cloneNode(true);
      document.body.appendChild(this.popup);

      this.popupVideo = this.popup.querySelector('.eg-video-carousel__popup-video');
      this.progressContainer = this.popup.querySelector('.eg-video-carousel__popup-progress');
      this.navPrev = this.popup.querySelector('.eg-video-carousel__popup-nav--prev');
      this.navNext = this.popup.querySelector('.eg-video-carousel__popup-nav--next');

      this.popup
        .querySelector('.eg-video-carousel__popup-close')
        .addEventListener('click', () => this.closePopup());
      this.popup
        .querySelector('.eg-video-carousel__popup-mute')
        .addEventListener('click', () => this.toggleMute());
      this.popup
        .querySelector('.eg-video-carousel__popup-playpause')
        .addEventListener('click', () => this.togglePlayPause());
      if (this.navPrev) this.navPrev.addEventListener('click', () => this.prevVideo());
      if (this.navNext) this.navNext.addEventListener('click', () => this.nextVideo(false));

      this.popupVideo.addEventListener('timeupdate', () => this.updateProgress());
      this.popupVideo.addEventListener('ended', () => this.nextVideo(true));
      this.popupVideo.addEventListener('play', () => this.popup.classList.remove('is-paused'));
      this.popupVideo.addEventListener('pause', () => this.popup.classList.add('is-paused'));
      this.popupVideo.addEventListener('playing', () => this.popup.classList.remove('is-loading'));

      this.buildProgressSegments();
    }

    buildProgressSegments() {
      this.progressContainer.innerHTML = '';
      this.progressSegments = this.videos.map(() => {
        const seg = document.createElement('span');
        seg.className = 'eg-video-carousel__popup-progress-segment';
        const fill = document.createElement('span');
        fill.className = 'eg-video-carousel__popup-progress-segment-fill';
        seg.appendChild(fill);
        this.progressContainer.appendChild(seg);
        return { segment: seg, fill };
      });
    }

    openPopup(index) {
      this.ensurePopup();
      if (!this.popup) return;
      this.currentIndex = index;
      this.popup.classList.add('is-active');
      this.loadCurrentVideo();
    }

    closePopup() {
      if (!this.popup) return;
      this.popup.classList.remove('is-active');
      try {
        this.popupVideo.pause();
        this.popupVideo.removeAttribute('src');
        this.popupVideo.innerHTML = '';
        this.popupVideo.load();
      } catch (e) {
        /* noop */
      }
      this.progressSegments.forEach((s) => {
        s.segment.classList.remove('is-done');
        s.fill.style.width = '0%';
      });
    }

    loadCurrentVideo() {
      const data = this.videos[this.currentIndex];
      if (!data || !data.sources || !data.sources.length) {
        this.nextVideo(true);
        return;
      }

      this.popup.classList.add('is-loading');

      this.progressSegments.forEach((s, i) => {
        const done = i < this.currentIndex;
        s.segment.classList.toggle('is-done', done);
        if (done) {
          s.fill.style.width = '100%';
        } else if (i === this.currentIndex) {
          s.fill.style.width = '0%';
        } else {
          s.fill.style.width = '0%';
        }
      });

      this.popupVideo.innerHTML = data.sources
        .map((s) => `<source src="${s.url}" type="${s.mime_type}">`)
        .join('');
      this.popupVideo.muted = this.muted;
      this.popup.classList.toggle('is-unmuted', !this.muted);
      this.popupVideo.load();
      const playPromise = this.popupVideo.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(() => {
          this.popupVideo.muted = true;
          this.muted = true;
          this.popup.classList.remove('is-unmuted');
          this.popupVideo.play().catch(() => {});
        });
      }
    }

    nextVideo(closeAtEnd) {
      if (this.currentIndex + 1 >= this.videos.length) {
        if (closeAtEnd) this.closePopup();
        return;
      }
      this.currentIndex += 1;
      this.loadCurrentVideo();
    }

    prevVideo() {
      if (this.currentIndex <= 0) return;
      this.currentIndex -= 1;
      this.loadCurrentVideo();
    }

    togglePlayPause() {
      if (!this.popupVideo) return;
      if (this.popupVideo.paused) {
        const p = this.popupVideo.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        this.popupVideo.pause();
      }
    }

    toggleMute() {
      this.muted = !this.muted;
      this.popupVideo.muted = this.muted;
      this.popup.classList.toggle('is-unmuted', !this.muted);
    }

    updateProgress() {
      if (!this.popupVideo.duration) return;
      const seg = this.progressSegments[this.currentIndex];
      if (!seg) return;
      const pct = (this.popupVideo.currentTime / this.popupVideo.duration) * 100;
      seg.fill.style.width = `${pct}%`;
    }
  }

  customElements.define('eg-video-carousel', EgVideoCarousel);
}
